import { useLayoutEffect, useRef, type ReactNode } from 'react'

function spaceBelow(element: HTMLElement) {
  let space = 0
  let node: HTMLElement | null = element
  while (node && !node.classList.contains('admin-page')) {
    let sibling = node.nextElementSibling
    while (sibling instanceof HTMLElement) {
      const style = getComputedStyle(sibling)
      space += sibling.offsetHeight
      space += Number.parseFloat(style.marginTop) || 0
      space += Number.parseFloat(style.marginBottom) || 0
      sibling = sibling.nextElementSibling
    }
    const parent: HTMLElement | null = node.parentElement
    if (!parent) break
    space += Number.parseFloat(getComputedStyle(parent).paddingBottom) || 0
    if (parent.classList.contains('admin-page')) break
    space += Number.parseFloat(getComputedStyle(parent).marginBottom) || 0
    node = parent
  }
  return space
}

export function ViewportScroll({ children, className }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return

    const fit = () => {
      if (element.getClientRects().length === 0) return
      const footer = document.querySelector('.admin-shell__footer')
      const footerHeight = footer instanceof HTMLElement ? footer.offsetHeight : 0
      const available = window.innerHeight - element.getBoundingClientRect().top - footerHeight - spaceBelow(element)
      const next = `${Math.max(160, Math.floor(available))}px`
      if (element.style.maxHeight !== next) element.style.maxHeight = next
    }

    fit()
    const observer = new ResizeObserver(fit)
    observer.observe(document.documentElement)
    observer.observe(element)
    window.addEventListener('resize', fit)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', fit)
    }
  }, [])

  return <div ref={ref} className={className}>{children}</div>
}

export function EditTableScroll({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return

    const fit = () => {
      const footer = document.querySelector('.admin-shell__footer')
      const footerHeight = footer instanceof HTMLElement ? footer.offsetHeight : 0
      const available = window.innerHeight - element.getBoundingClientRect().top - footerHeight - spaceBelow(element)
      element.style.maxHeight = `${Math.max(160, Math.floor(available))}px`
      const table = element.querySelector('table')
      table?.classList.toggle('border-end', element.scrollHeight - element.clientHeight > 1)
    }

    fit()
    const observer = new ResizeObserver(fit)
    observer.observe(document.documentElement)
    const table = element.querySelector('table')
    if (table) observer.observe(table)
    window.addEventListener('resize', fit)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', fit)
    }
  }, [])

  return <div ref={ref} className="edit-table-scroll border rounded-3">{children}</div>
}
